/**
 * Prerelease guides, keyed by set code.
 *
 * Written by hand per set — editorial, not something derivable from card data.
 * Kept in English on purpose: card names, mechanics and rules text are English
 * on the cards themselves, and a guide that switches language mid-sentence is
 * harder to read at a table than one that doesn't.
 *
 * The shape is deliberately small. Headings, prose, bullets and the occasional
 * table are enough for something read standing up, and anything richer would
 * mean a markdown renderer and a sanitiser for one page.
 */

export type GuideBullet = { lead?: string; text: string };

export type GuideSection = {
  id: string;
  heading: string;
  body?: string[];
  bullets?: GuideBullet[];
  table?: { head: string[]; rows: string[][] };
  /** Set apart — the one thing worth remembering from the section. */
  callout?: string;
};

export type SetGuide = {
  code: string;
  standfirst: string;
  sourceNote: string;
  sections: GuideSection[];
};

const realityFracture: SetGuide = {
  code: 'FRA',
  standfirst:
    'The three mechanics in plain words, where the removal actually is, the ten colour pairs, the bombs, the one real combo, and the rules questions you will be asked on the night.',
  sourceNote:
    'Card names, costs and rules text checked against Scryfall card by card. Ratings and archetype reads are judgment, not data.',
  sections: [
    {
      id: 'mechanics',
      heading: 'The three mechanics',
      body: [
        'Two are new and one is the glue. Teach them in this order, because the other two lean on surveil.',
      ],
      bullets: [
        {
          lead: 'Surveil, on 43 cards',
          text:
            'Look at the top N, put any number in the graveyard, the rest back on top. It is not card draw — that is the commonest beginner mistake. It fills the graveyard, which threshold, flashback and recursion cards all care about.',
        },
        {
          lead: 'Empower Jace N',
          text:
            'Put N loyalty counters on a Jace token you control, creating one first if you have none. The token starts at 0 loyalty and has only minus abilities: −1 surveil 1, −3 draw a card. You never get a second one; later empowers stack onto the Jace you already have.',
        },
        {
          lead: 'Prepare, the one that wins games',
          text:
            'Two-faced cards with a creature in front and a spell behind. Cast the creature, then later cast a copy of its spell for that spell’s own cost. The creature stays on the battlefield and simply stops being prepared, so every one of these is a two-for-one.',
        },
      ],
      callout:
        'If a prepared creature is about to be killed, cast its spell in response. You keep the value and the removal is still spent — the most useful single thing to tell a newer player all night.',
    },
    {
      id: 'removal',
      heading: 'Where the removal actually is',
      body: [
        'This is what decides sealed, and it is lopsided. Counting every common and uncommon that kills, exiles or shrinks a creature: red has six and most are unconditional, black has five that are cheap and clean, white has five that all carry conditions, green has one that only hits fliers, and blue has a single soft counter.',
        'Open your pool with red or black and you are most of the way to a deck. Blue and green are support colours here — they have the card advantage and the bodies, but almost nothing that answers a bomb. Somebody who builds blue-green and loses to the first large creature has not been unlucky; they built a deck with no outs.',
      ],
      table: {
        head: ['Card', 'Cost', 'What it does'],
        rows: [
          ['[[No Admittance]]', '{1}{R}', '3 damage to any target and empower Jace 1. The best common removal in the set.'],
          ['[[Awaken the Inferno]]', '{4}{R}', '6 damage to a creature or planeswalker an opponent controls.'],
          ['[[Violent Echoes]]', '{2}{R}{R}', '6 damage, with excess carrying over.'],
          ['[[Last Gasp]]', '{1}{B}', '−3/−3 at instant speed for two mana. Kills most of what you will face.'],
          ['[[Extended Absence]]', '{3}{B}', 'Exiles a creature or planeswalker and drains 1.'],
          ['[[Terminal Criticism]]', '{1}{B}', 'Destroys a blue or red creature or planeswalker. Check their colours first.'],
          ['[[Prophesied End]]', '{1}{W}', 'Destroy a creature; if it was not attacking its controller draws. White’s cleanest answer.'],
          ['[[Your Fate Ends Here]]', '{2}{W}', 'Only hits mana value 3 or greater, so it is dead against aggro.'],
          ['[[Surgical Precision]]', '{1}{W}', 'Only kills toughness 4 or greater.'],
          ['[[Refute Destiny]]', '{1}{W}', 'Exiles a green or blue creature or planeswalker.'],
          ['[[Sureshot Sower]]', '{1}{G}', 'Discard it to destroy a flier. Green’s only answer, and it is narrow.'],
        ],
      },
    },
    {
      id: 'pairs',
      heading: 'The ten colour pairs',
      body: ['Each pair has a gold uncommon or two that tells you what it wants.'],
      bullets: [
        { lead: 'White-Blue', text: 'Surveil into Jace control. [[Fatehold Charm]], [[Desperate Futurescribe]], [[Prudent Fateseer]].' },
        { lead: 'Blue-Black', text: 'Graveyard and theft. [[Theorix Charm]], [[Paradox Shaper]], [[Recursive Recruitment]].' },
        { lead: 'Black-Red', text: 'Aggression and sacrifice. [[Stingerquill Charm]] and [[Grim Repriser]] — the two removal colours together.' },
        { lead: 'Red-Green', text: 'Heartwood artifacts. [[Konstrari Charm]], [[Craftwork Crusher]]; Heartwood tokens tap for red or green.' },
        { lead: 'Green-White', text: 'Lifegain and counters. [[Vigorbloom Charm]], [[Bloombrute]].' },
        { lead: 'White-Black', text: 'Value from things dying. [[Edgar, Ancient Bloodlord]] and [[Twisted Fates]].' },
        { lead: 'Blue-Red', text: 'Spells and thopters. [[Saheeli, Jewel of Avishkar]]; [[Clash of Elements]].' },
        { lead: 'Black-Green', text: 'Mill into −1/−1. [[Hapatra, the Desert Fang]] scales off your graveyard.' },
        { lead: 'Green-Blue', text: 'Landfall into Jace. [[Avatar of Burgeoning Echoes]] empowers 2 per land, for two mana.' },
        { lead: 'Red-White', text: 'Equipment aggro. [[Warrior’s Blades]] is removal and a threat at once.' },
      ],
    },
    {
      id: 'bombs',
      heading: 'Bombs',
      body: ['In sealed a bomb is a card that wins alone if unanswered — a much lower bar than constructed.'],
      bullets: [
        { lead: '[[Overwrite the Multiverse]]', text: 'Exiles all creatures and empowers Jace for each one. The hardest card in the set to beat.' },
        { lead: '[[Verdant Kraken]]', text: 'A 6/6 that makes a 3/3 land creature every upkeep. Wins any long game.' },
        { lead: '[[Emrakul, the Exigent Doom]]', text: 'Genuinely castable: exile it from hand for {3} to upgrade a land, then cast it from exile.' },
        { lead: '[[The Theorist, Jace Beleren]]', text: 'A real planeswalker that draws a card on every opponent’s draw step.' },
        { lead: '[[Denzilore Fatehold]]', text: 'Flash flier that counters up the whole team on every scry or surveil — in a set with 43 surveil cards.' },
        { lead: '[[The Echoverse Fulcrum]]', text: 'Two-mana colourless artifact that later becomes "destroy all creatures". Fits any pool.' },
        { lead: '[[Tarmogoyf]]', text: 'Yes, it is in here. In sealed it is a two-mana 3/4 — good, not a trophy. Worth saying to whoever opens one.' },
      ],
    },
    {
      id: 'combo',
      heading: 'The one real combo',
      body: [
        '[[Hapatra, the Desert Frost]] plus [[Loot, the Nexus]] is infinite mana, and the maths checks out. Loot taps for one mana of a chosen colour per different power among your creatures; Hapatra untaps any creature for {2}{U}. Tap Loot for blue, spend {2}{U} to untap it, repeat.',
        'It nets mana at four or more distinct power values on board. Loot is a 2/1 and Hapatra a 4/3, so you need two more creatures whose powers differ from those and from each other.',
      ],
      callout:
        'Infinite mana does not win on its own — neither card is a sink, so it also needs an X spell or a big activated ability. And it needs two specific uncommons from two colours in one pool. Note there are two Hapatras in the set: the Desert Frost is blue and is the combo piece, the Desert Fang is black-green and is not.',
    },
    {
      id: 'rules',
      heading: 'Rules questions you will be asked',
      table: {
        head: ['They ask', 'Answer'],
        rows: [
          ['I already have a Jace — do I get another?', 'No. The counters go on the Jace you control; you only create one when you control none.'],
          ['Is the Jace token legendary?', 'No. Token Planeswalker — Jace. The legend rule never comes up.'],
          ['Can I attack their Jace?', 'Yes, it is a planeswalker, and combat damage removes loyalty.'],
          ['Do I sacrifice the creature to cast its prepared spell?', 'No. It stays on the battlefield and simply becomes unprepared.'],
          ['When can I cast a prepared spell?', 'Whenever you could cast a spell of that type. Nearly all of them are sorceries.'],
          ['My prepared creature died — can I still cast it?', 'No. It has to be on the battlefield and prepared.'],
          ['Can they counter the prepared spell?', 'Yes. You cast a copy, and it uses the stack like any other spell.'],
          ['Is surveil the same as scry?', 'No. Scry puts cards on the bottom, surveil puts them in the graveyard.'],
          ['My Jace is at 3 — can I use −3?', 'Yes, and it dies at 0 loyalty. That is fine; you drew the card.'],
        ],
      },
    },
    {
      id: 'coach',
      heading: 'Coaching a build in five minutes',
      body: ['Some people at a prerelease have never built a sealed deck. This order works for any set.'],
      bullets: [
        { lead: 'Sort by colour, not rarity', text: 'Rares distract. Count playables per colour — creatures and removal you would happily cast.' },
        { lead: 'Find the removal first', text: 'In this set that usually settles one colour before they have looked at anything else.' },
        { lead: 'Fourteen to sixteen creatures', text: 'With a curve: a few twos, more threes and fours, two or three fives, rarely anything above six.' },
        { lead: 'Prepared creatures count double', text: 'Each is a creature and a spell, so the deck has more going on than its creature count suggests.' },
        { lead: 'Twenty-three spells, seventeen lands', text: 'Right nearly always. Cycling lands make a light splash safer here — but only for a bomb.' },
        { lead: 'Jace tokens make games longer', text: 'Both players carry extra effective life, so card advantage beats speed more often than usual.' },
      ],
    },
  ],
};

const GUIDES: Record<string, SetGuide> = { [realityFracture.code]: realityFracture };

/** A guide for a set code, if one has been written. */
export function guideFor(code: string | null | undefined): SetGuide | null {
  if (!code) return null;
  return GUIDES[code.trim().toUpperCase()] ?? null;
}

export function hasGuide(code: string | null | undefined): boolean {
  return guideFor(code) !== null;
}
